import type { Object3D, OrthographicCamera, PerspectiveCamera, Scene } from 'three'
import { Box3, EventDispatcher, MOUSE, MathUtils, TOUCH, Vector3 } from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import type { MapControls } from 'three/examples/jsm/controls/MapControls.js'
import * as TWEEN from '@tweenjs/tween.js'
import { use } from '../../export'
import type { ISpawnPoint } from './CharacterControls'
import { CharacterControls } from './CharacterControls'

/**
 * 控制器的模式
 */
export enum EControlMode {
  Orbit = 0, // 轨道控制器 ：鼠标左键拖动旋转，鼠标右键拖动平移，滚轮缩放, WSAD控制前后左右
  Map = 1, // 地图控制器 ：鼠标左键拖动平移，鼠标右键拖动旋转，滚轮缩放, WASD控制前后左右
  First = 2, // 第一人称控制器 ：鼠标左键拖动旋转，滚轮缩放, WASD控制前后左右, Shift奔跑, +|-移动速度
  Third = 3, // 第三人称控制器 ：鼠标左键拖动旋转，滚轮缩放, WASD控制前后左右, Shift奔跑, +|-移动速度
  Fly = 4, // 飞行控制器 ：鼠标左键拖动旋转，滚轮缩放,  WASD控制前后左右, EQ控制上升下降, Shift加速, +|-移动速度
}

export interface UniversalControlsOptions {
  scene: Scene
  camera: PerspectiveCamera | OrthographicCamera
  domElement: HTMLElement
  mode?: EControlMode
  characterModelUrl?: string
  spawnPoint?: ISpawnPoint
  sceneControl?: any
  limit?: {
    pitch?: [number, number]
    yaw?: [number, number]
    distance?: [number, number]
  }
  target?: [number, number, number]
}
export type ControlsType = OrbitControls | MapControls | CharacterControls | null
export type ControlsOption = {
  mode: {
    type: EControlMode
  }
  limit?: {
    pitch?: [number, number]
    yaw?: [number, number]
    distance?: [number, number]
  }
}
/**
 * 通用控制器
 *
 * @export
 *
 * @
 */
export class UniversalControls extends EventDispatcher {
  private scene: Scene
  private _camera: PerspectiveCamera | OrthographicCamera
  private domElement: HTMLElement
  private options: any
  private currentTween: TWEEN.Tween<Vector3> | null = null
  private sceneControl?: any

  private controls: {
    orbit: OrbitControls
    character: CharacterControls
  }

  public activeControl: ControlsType
  private _mode: EControlMode

  constructor(options: UniversalControlsOptions) {
    super()
    this.scene = options.scene
    this._camera = options.camera
    this.domElement = options.domElement
    this._mode = options.mode || EControlMode.Orbit
    this.options = options
    this.activeControl = null
    this.sceneControl = options.sceneControl

    this.controls = {
      orbit: new OrbitControls(this._camera, this.domElement),
      character: new CharacterControls({ scene: this.scene, camera: this._camera, domElement: this.domElement, characterModelUrl: this.options.characterModelUrl, spawnPoint: this.options.spawnPoint, limit: this.options.limit }),
    }
    const { pitch, yaw, distance } = this.options.limit || {}
    if (pitch?.length > 1) {
      this.controls.orbit.minPolarAngle = MathUtils.degToRad(pitch[0])
      this.controls.orbit.maxPolarAngle = MathUtils.degToRad(pitch[1])
    }
    if (yaw?.length > 1) {
      this.controls.orbit.minAzimuthAngle = MathUtils.degToRad(yaw[0])
      this.controls.orbit.maxAzimuthAngle = MathUtils.degToRad(yaw[1])
    }
    if (distance?.length > 1) {
      this.controls.orbit.minDistance = distance[0]
      this.controls.orbit.maxDistance = distance[1]
    }
    this.controls.orbit.enableDamping = false
    this.controls.orbit.dampingFactor = 0.1
    this.controls.orbit.target.copy(new Vector3().fromArray(options.target || [0, 0, 0]))
    this.controls.orbit.keyPanSpeed = 10
    this.controls.orbit.zoomSpeed = 5
    this.controls.orbit.listenToKeyEvents(this.domElement)
    this.controls.orbit.screenSpacePanning = false
    this.controls.orbit.keys = { LEFT: 'KeyA', UP: 'KeyW', RIGHT: 'KeyD', BOTTOM: 'KeyS' }
    this.controls.orbit.addEventListener('change', () => {
      this.dispatchEvent({ type: 'change' })
    })
    this.controls.character.addEventListener('change', () => {
      this.dispatchEvent({ type: 'change' })
    })
    // this.addEventListener("change", this.onChange.bind(this));

    this.controls.character.enabled = false

    this.setMode(this._mode)
    if (this.sceneControl) {
      use.useframe(this.sceneControl, () => {
        this.update()
      })
    }
  }

  public onChange() {
    console.log('onChange..')
    const camera = this._camera
    // if(this.mode===EControlMode.Orbit || this.mode===EControlMode.Map){
    const polar = Math.max(this.getPolarAngle(), 0.1)
    const dist = Math.max(this.getDistance(), 100)
    const far = Math.max((dist / polar) * 8, 200000) // 确保远裁剪面至少为 10000
    camera.far = MathUtils.clamp(far, 250000, 50000 * 6000) // 设置远裁剪面的范围
    camera.near = Math.max(camera.far / 15000, 0.01) // 确保近裁剪面不会过小
    camera.updateProjectionMatrix()
    // }
  }

  //  设置焦距
  public setFocusDistance(object: Object3D) {
    object.updateMatrixWorld()
    const box = new Box3().setFromObject(object)
    const size = box.getSize(new Vector3()).length()
    const center = box.getCenter(new Vector3())
    // this.controls.reset();
    object.position.x -= center.x
    object.position.y -= center.y
    object.position.z -= center.z

    this._camera.near = size / 100
    this._camera.far = size * 100
    this._camera.updateProjectionMatrix()
    this._camera.position.copy(center)
    this._camera.position.x += size / 2.0
    this._camera.position.y += size / 5.0
    this._camera.position.z += size / 2.0
    this._camera.lookAt(center)
  }

  public get mode(): EControlMode {
    return this._mode
  }

  public set mode(value: EControlMode) {
    this.setMode(value)
  }

  public get enabled(): boolean {
    return this.activeControl!.enabled
  }

  public set enabled(value: boolean) {
    this.activeControl!.enabled = value
  }

  public get(): ControlsOption {
    return {
      mode: { type: this.mode },
      limit: this.options.limit,
    }
  }

  public set(option: ControlsOption) {
    if (!option)
      return

    // 切换模式
    if (option.mode && option.mode.type !== undefined)
      this.setMode(option.mode.type)

    // Orbit/Map
    if ((this._mode === EControlMode.Orbit || this._mode === EControlMode.Map) && option.limit) {
      if (option.limit.pitch) {
        this.controls.orbit.minPolarAngle = option.limit.pitch[0] ?? 0
        this.controls.orbit.maxPolarAngle = option.limit.pitch[1] ?? Math.PI
      }
      if (option.limit.yaw) {
        this.controls.orbit.minAzimuthAngle = option.limit.yaw[0] ?? -Infinity
        this.controls.orbit.maxAzimuthAngle = option.limit.yaw[1] ?? Infinity
      }
      if (option.limit.distance) {
        this.controls.orbit.minDistance = option.limit.distance[0] ?? 0.01
        this.controls.orbit.maxDistance = option.limit.distance[1] ?? 80 * 1000 * 1000
      }
    }

    // CharacterControls
    if ((this._mode === EControlMode.First || this._mode === EControlMode.Third || this._mode === EControlMode.Fly) && option.limit)
      this.controls.character.limit = option.limit
      // if (option.limit.distance) {
      //   // 第三人称/飞行可以设置 cameraDistance
      //   this.controls.character.cameraDistance = option.limit.distance[0]
      // }
  }

  public setMode(mode: EControlMode): void {
    if (this.activeControl)
      this.activeControl.enabled = false // 禁用当前激活的控制器

    if (this.currentTween) {
      this.currentTween.stop() // 终止未完成的动画
      this.currentTween = null
    }

    switch (mode) {
      case EControlMode.Orbit:// 轨道控制器
        this.activeControl = this.controls.orbit
        this.activeControl.enabled = true

        this.activeControl.mouseButtons = { LEFT: MOUSE.ROTATE, MIDDLE: MOUSE.DOLLY, RIGHT: MOUSE.PAN }
        this.activeControl.touches = { ONE: TOUCH.ROTATE, TWO: TOUCH.DOLLY_PAN }
        // this.currentTween = new TWEEN.Tween(this.activeControl.object.position).to({ ...this.controls.character.position }, 1000).onComplete(() => this.currentTween = null).start()

        break
      case EControlMode.Map:// 地图控制器
        this.activeControl = this.controls.orbit
        this.activeControl.enabled = true

        this.activeControl.mouseButtons = { LEFT: MOUSE.PAN, MIDDLE: MOUSE.DOLLY, RIGHT: MOUSE.ROTATE }
        this.activeControl.touches = { ONE: TOUCH.PAN, TWO: TOUCH.DOLLY_ROTATE }
        // this.currentTween = new TWEEN.Tween(this.activeControl.object.position).to({ ...this.controls.character.character!.position }, 1000).onComplete(() => this.currentTween = null).start()
        break
      case EControlMode.First:// 第一人称控制器
        this.activeControl = this.controls.character
        this.activeControl.enabled = true
        this.activeControl.allowFly = false
        this.activeControl.maxMoveSpeed = 0.4
        this.activeControl.moveSpeed = 0.1
        if (this.activeControl.character && this.activeControl.character.position)
          this.currentTween = new TWEEN.Tween(this.activeControl.character.position).to({ y: this.options.spawnPoint.position.y }, 1000).onComplete(() => this.currentTween = null).start()

        this.activeControl.setMode(true)
        break
      case EControlMode.Third:// 第三人称控制器
        this.activeControl = this.controls.character
        this.activeControl.enabled = true
        this.activeControl.allowFly = false
        this.activeControl.maxMoveSpeed = 0.4
        this.activeControl.moveSpeed = 0.1
        this.activeControl.setMode(false)
        if (this.activeControl.character && this.activeControl.character.position)
          this.currentTween = new TWEEN.Tween(this.activeControl.character.position).to({ y: this.options.spawnPoint.position.y }, 1000).onComplete(() => this.currentTween = null).start()
        break
      case EControlMode.Fly:// 飞行控制器
        this.activeControl = this.controls.character
        this.activeControl.enabled = true
        this.activeControl.allowFly = true
        this.activeControl.maxMoveSpeed = 8
        this.activeControl.moveSpeed = 1
        this.activeControl.setMode(true)
        if (this.activeControl.character && this.activeControl.character.position)
          this.currentTween = new TWEEN.Tween(this.activeControl.character.position).to({ y: 200 }, 1000).onComplete(() => this.currentTween = null).start()
        break
    }

    if (this.sceneControl)
      this.sceneControl.context.setControls(this.activeControl)

    this._mode = mode
  }

  public update(): void {
    if (this.activeControl && this.activeControl.update) { // 确保 update 方法存在
      this.activeControl.enabled && this.activeControl.update()
    }
  }

  public dispose(): void {
    // 释放当前激活的控制器
    if (this.activeControl) {
      this.activeControl.dispose()
      this.activeControl = null
    }

    // 释放所有控制器实例
    Object.values(this.controls).forEach((control) => {
      if (control && control.dispose)
        control.dispose()
    })
  }

  // -----------------兼容适配City，待测试验证---------------------------
  // 获取当前相机距离
  public getDistance(): number {
    if (this._mode === EControlMode.Orbit || this._mode === EControlMode.Map)
      return this.controls.orbit.getDistance()

    else if (this._mode === EControlMode.First || this._mode === EControlMode.Third)
      return this.controls.character.cameraDistance
    else
      return this.controls.character.getDistance(this.controls.orbit.target)
  }

  public get camrea(): PerspectiveCamera | OrthographicCamera {
    return this._camera
  }

  public get target(): Vector3 | undefined {
    if (this._mode === EControlMode.Orbit || this._mode === EControlMode.Map)
      return this.controls.orbit.target

    else
      return this.controls.character.target
  }

  public getAzimuthalAngle(): number {
    if (this._mode === EControlMode.Orbit || this._mode === EControlMode.Map)
      return this.controls.orbit.getAzimuthalAngle()

    else
      return this.controls.character.getAzimuthalAngle()
  }

  public getPolarAngle(): number {
    if (this._mode === EControlMode.Orbit || this._mode === EControlMode.Map)
      return this.controls.orbit.getPolarAngle()

    else
      return this.controls.character.getPolarAngle()
  }
}
